import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box/Box';
import Stack from '@mui/material/Stack/Stack';
import Card from '@mui/material/Card/Card';
import CardContent from '@mui/material/CardContent/CardContent';
import Typography from '@mui/material/Typography/Typography';
import Button from '@mui/material/Button/Button';
import Chip from '@mui/material/Chip/Chip';
import Alert from '@mui/material/Alert/Alert';
import IconButton from '@mui/material/IconButton/IconButton';
import Tooltip from '@mui/material/Tooltip/Tooltip';
import Switch from '@mui/material/Switch/Switch';
import FormControlLabel from '@mui/material/FormControlLabel/FormControlLabel';
import { alpha, useTheme } from '@mui/material/styles';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import StopIcon from '@mui/icons-material/Stop';
import ReplayIcon from '@mui/icons-material/Replay';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { DragDropContext, Draggable, Droppable, DropResult } from '@hello-pangea/dnd';
import { HackathonPayload } from '../../services/hackathon.service';
import { SessionPayload } from '../../services/session.service';
import { nextPresenterChanges, SessionLiveChanges, SessionLiveState, updateLiveSession } from '../../services/sessionLive.service';
import { useLiveBoard } from '../../hooks/useLiveSessions';
import { useSessions } from '../../hooks/useSessions';
import { disableVotingPeriod, enableVotingPeriod } from '../../services/hackathon.service';
import { LiveDot } from './LiveNowStrip';
import { useElapsed } from './useElapsed';
import { HelpLink } from '../HelpLink';

/**
 * The session's presenting order and what's on now. Hosts and organizers run the room from here.
 */
export const SessionRunOfShow = ({
    hackathonId,
    hackathon,
    session,
    canRun,
    viewerEmail
}: {
    hackathonId: string,
    hackathon: HackathonPayload,
    session: SessionPayload,
    canRun: boolean,
    viewerEmail?: string
}) => {
    const theme = useTheme();
    const board = useLiveBoard(hackathonId);
    const liveList = board.sessions;
    const { sessions } = useSessions(hackathonId);
    // with one room its own switch is ignored: the hackathon-wide switch (organizers only) decides
    const single = sessions.length <= 1;
    const isOrganizer = !!viewerEmail && hackathon.organizers.some(o => o.email === viewerEmail);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    // a dropped row stays where it was dropped while the new order saves, instead of snapping back for a poll
    const [pendingOrder, setPendingOrder] = useState<string[] | null>(null);

    const live: SessionLiveState = liveList.find(s => s._id === session._id) || {
        _id: session._id,
        slug: session.slug,
        name: session.name,
        ideas: session.ideas,
        activeIdea: null,
        activeSince: null,
        presented: [],
        leftOut: [],
        votingOpen: false,
        liveUpdatedAt: null,
        descriptionUpdatedAt: session.descriptionUpdatedAt
    };
    const elapsed = useElapsed(live.activeSince);
    const sameTeams = (a: string[], b: string[]) => a.length === b.length && a.every(id => b.includes(id));
    const order = pendingOrder && sameTeams(pendingOrder, live.ideas) ? pendingOrder : live.ideas;
    React.useEffect(() => {
        if (pendingOrder && (pendingOrder.join() === live.ideas.join() || !sameTeams(pendingOrder, live.ideas))) {
            setPendingOrder(null);
        }
    }, [live.ideas.join(), pendingOrder]);

    const teamFor = (id: string) => hackathon.ideas.find(i => i._id === id);
    const active = live.activeIdea ? teamFor(live.activeIdea) : undefined;
    const presented = new Set(live.presented);
    const next = nextPresenterChanges(live);
    const nextTeam = next.activeIdea ? teamFor(next.activeIdea) : undefined;
    const allDone = live.ideas.length > 0 && live.ideas.every(id => presented.has(id));
    // votes are only accepted while hackathon-wide voting is on (and, with several rooms, the room's switch)
    const votingLive = board.votingPeriodOn && (single || live.votingOpen);
    const hasVotingSwitch = canRun && (!single || isOrganizer);

    const apply = (changes: SessionLiveChanges) => {
        setBusy(true);
        setError('');
        updateLiveSession(hackathonId, session._id, changes).catch(err => {
            setError(err.response?.data?.message || 'Could not update the session');
        }).finally(() => setBusy(false));
    };

    const toggleHackathonVoting = (on: boolean) => {
        setBusy(true);
        setError('');
        (on ? enableVotingPeriod : disableVotingPeriod)(hackathon._id).catch(err => {
            setError(err.response?.data?.message || 'Could not change voting');
        }).finally(() => setBusy(false));
    };

    // presentations happen with votes: starting the show opens voting (the hackathon's switch if the host may flip it,
    // and the room's own), so people score each team while it's fresh. The switch stays, to turn it off again.
    const resultsOut = hackathon.winners.length > 0;
    const opensVoting = !active && !votingLive && !resultsOut && (isOrganizer || (!single && board.votingPeriodOn));
    const start = () => {
        apply(!single && !live.votingOpen && !resultsOut ? { ...next, votingOpen: true } : next);
        if (isOrganizer && !board.votingPeriodOn && !resultsOut) {
            toggleHackathonVoting(true);
        }
    };

    const reorder = (from: number, to: number) => {
        if (to < 0 || to >= order.length || from === to) return;
        const next = [...order];
        const [item] = next.splice(from, 1);
        next.splice(to, 0, item);
        setPendingOrder(next);
        apply({ ideas: next });
    };
    const move = (index: number, delta: number) => reorder(index, index + delta);
    const onDragEnd = (result: DropResult<string>) => {
        if (result.destination) {
            reorder(result.source.index, result.destination.index);
        }
    };

    // the hosts pick the lineup: any idea can present and be judged, team or not, and any can sit this one out
    const leaveOut = (id: string) => apply({ leftOut: [...live.leftOut, id] });
    // pitches nobody joined (often abandoned when the author moved to another team) can still present; offer to leave
    // them out right where the host is, instead of the host spotting them in the list (runs 22-25)
    const unstaffed = live.ideas.filter(id => !presented.has(id) && id !== live.activeIdea && teamFor(id)?.members.length === 0);
    const putBack = (id: string) => apply({ leftOut: live.leftOut.filter(l => l !== id) });

    const togglePresented = (id: string) => {
        apply({ presented: presented.has(id) ? live.presented.filter(p => p !== id) : [...live.presented, id] });
    };

    return (
        <Stack spacing={2}>
            <Card sx={active ? { borderColor: 'primary.main', borderWidth: 2, backgroundColor: alpha(theme.palette.primary.main, 0.04) } : undefined}>
                <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                    <Stack spacing={2}>
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap>
                            <Stack direction="row" spacing={1} alignItems="center">
                                {active && <LiveDot />}
                                <Typography variant="overline" sx={{ fontWeight: 700 }}>{active ? 'Now presenting' : allDone ? 'All teams presented' : live.presented.length > 0 ? 'Between teams' : 'Not presenting yet'}</Typography>
                            </Stack>
                            <Stack direction="row" spacing={1} alignItems="center">
                                {active && elapsed && <Chip size="small" variant="outlined" label={elapsed} sx={{ fontVariantNumeric: 'tabular-nums' }} />}
                                {votingLive && !hasVotingSwitch && <Chip size="small" color="secondary" label="Voting open" />}
                                {!single && live.votingOpen && !votingLive && (
                                    <Tooltip title="This room is ready for votes, but hackathon-wide voting is off. An organizer needs to open it.">
                                        <Chip size="small" variant="outlined" label="Voting ready" />
                                    </Tooltip>
                                )}
                            </Stack>
                        </Stack>

                        {
                            active ? (
                                <Box>
                                    <Typography variant="h2" component="p" sx={{ fontSize: { xs: '1.75rem', md: '2.25rem' }, overflowWrap: 'anywhere' }}>
                                        <Link to={`/h/${hackathon.slug}/idea/${active._id}`} style={{ color: 'inherit' }}>{active.title}</Link>
                                    </Typography>
                                    {active.members.length > 0
                                        ? <Typography color="text.secondary">{active.members.map(m => m.username).join(', ')}</Typography>
                                        : <Chip size="small" color="warning" variant="outlined" label="No members yet" sx={{ alignSelf: 'flex-start' }} />}
                                </Box>
                            ) : (
                                <Typography color="text.secondary">
                                    {allDone ? 'Every team has presented.' : canRun ? 'Start when the first team is ready.' : 'The hosts will start presentations here.'}
                                </Typography>
                            )
                        }

                        {
                            !allDone && nextTeam && (
                                <Typography variant="body2" color="text.secondary">
                                    {active ? 'Up next: ' : 'First up: '}<b>{nextTeam.title}</b>
                                </Typography>
                            )
                        }

                        {
                            canRun && !allDone && unstaffed.length > 0 && (
                                // one choice per idea: a real team whose members haven't joined yet mustn't be swept out with a junk pitch (run026)
                                <Alert severity="info">
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        Nobody is on {unstaffed.length === 1 ? 'this idea' : 'these ideas'} yet. {unstaffed.length === 1 ? 'It' : 'Each'} can still present, or you can leave {unstaffed.length === 1 ? 'it' : 'any of them'} out.
                                    </Typography>
                                    <Stack spacing={0.5}>
                                        {unstaffed.map(id => (
                                            <Stack key={id} direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                                                <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: 'anywhere', minWidth: 0 }}>{teamFor(id)?.title}</Typography>
                                                <Button color="inherit" size="small" disabled={busy} sx={{ flexShrink: 0 }}
                                                    aria-label={`Leave out: ${teamFor(id)?.title}`} onClick={() => leaveOut(id)}>Leave out</Button>
                                            </Stack>
                                        ))}
                                    </Stack>
                                </Alert>
                            )
                        }
                        {
                            canRun && (
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} flexWrap="wrap" useFlexGap>
                                    {
                                        (nextTeam || active) && (
                                            <Button
                                                variant="contained"
                                                size="large"
                                                disabled={busy}
                                                startIcon={active ? <SkipNextIcon /> : <PlayArrowIcon />}
                                                onClick={() => active ? apply(next) : start()}>
                                                {active ? (nextTeam ? 'Next team' : 'Finish') : 'Start'}
                                            </Button>
                                        )
                                    }
                                    {
                                        active && (
                                            <Button size="large" disabled={busy} startIcon={<StopIcon />} onClick={() => apply({ activeIdea: null })}>
                                                Pause
                                            </Button>
                                        )
                                    }
                                    {
                                        allDone && !active && (
                                            <Button size="large" variant="outlined" disabled={busy} startIcon={<ReplayIcon />} onClick={() => apply({ presented: [] })}>
                                                Start over
                                            </Button>
                                        )
                                    }
                                    <Box sx={{ flex: 1 }} />
                                    {
                                        single ? (
                                            isOrganizer && (
                                                <FormControlLabel
                                                    control={<Switch checked={board.votingPeriodOn} disabled={busy} onChange={e => toggleHackathonVoting(e.target.checked)} inputProps={{ 'aria-label': 'Voting open' }} />}
                                                    // the words say the state: "Voting open" on a closed switch read as a status (Oliver, runs 27-28)
                                                    label={board.votingPeriodOn ? 'Voting open' : 'Voting closed'} />
                                            )
                                        ) : (
                                            <Stack>
                                                <FormControlLabel
                                                    control={<Switch checked={live.votingOpen} disabled={busy} onChange={e => apply({ votingOpen: e.target.checked })} />}
                                                    label="Voting open in this room" />
                                                {live.votingOpen && !board.votingPeriodOn && (
                                                    <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 320 }}>
                                                        Hackathon-wide voting is off, so votes won't be accepted until an organizer opens it.
                                                    </Typography>
                                                )}
                                            </Stack>
                                        )
                                    }
                                </Stack>
                            )
                        }
                        {
                            canRun && !active && !allDone && (opensVoting
                                ? <Typography variant="body2" color="text.secondary">Starting also opens voting, so everyone can score each team as it presents.</Typography>
                                : !votingLive && !resultsOut && <Typography variant="body2" color="text.secondary">Voting is off: an organizer opens it for the hackathon.</Typography>)
                        }
                        {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
                    </Stack>
                </CardContent>
            </Card>

            <Card>
                <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                    <Stack spacing={2}>
                        <Stack direction="row" spacing={1} alignItems="baseline" justifyContent="space-between">
                            <Typography variant="h4" component="h2">Presenting order</Typography>
                            <Stack direction="row" spacing={1} alignItems="baseline">
                                {live.ideas.length > 0 && (
                                    <Typography variant="body2" color="text.secondary">{live.presented.length} of {live.ideas.length} presented</Typography>
                                )}
                                {canRun && <HelpLink guide="present">Running the room</HelpLink>}
                            </Stack>
                        </Stack>
                        {
                            canRun && (
                                <Typography variant="body2" color="text.secondary">
                                    Drag teams by the handle to change the order. Every idea here presents and gets judged, with or without a team; leave out any that won't present.
                                </Typography>
                            )
                        }
                        {
                            live.ideas.length === 0
                                ? <Typography color="text.secondary">No teams assigned yet.</Typography>
                                : (
                                    <DragDropContext onDragEnd={onDragEnd}>
                                    <Droppable droppableId={`lineup-${session._id}`} isDropDisabled={!canRun}>
                                    {(drop, dropSnapshot) => (
                                    <Stack component="ol" spacing={1} ref={drop.innerRef} {...drop.droppableProps}
                                        sx={{ listStyle: 'none', p: 0, m: 0, borderRadius: '12px', outline: dropSnapshot.isDraggingOver ? `2px dashed ${theme.palette.divider}` : undefined, outlineOffset: 4 }}>
                                        {order.map((id, index) => {
                                            const team = teamFor(id);
                                            if (!team) {
                                                return null;
                                            }
                                            const isActive = id === live.activeIdea;
                                            const isDone = presented.has(id);
                                            const isMine = !!viewerEmail && team.members.some(m => m.email === viewerEmail);
                                            return (
                                                <Draggable key={id} draggableId={id} index={index} isDragDisabled={!canRun || busy}>
                                                {(drag, dragSnapshot) => (
                                                <Stack component="li" direction="row" spacing={1.5} alignItems="center" ref={drag.innerRef} {...drag.draggableProps} sx={{
                                                    p: 1.25,
                                                    pl: canRun ? 0.5 : 1.5,
                                                    borderRadius: '12px',
                                                    backgroundColor: isActive ? alpha(theme.palette.primary.main, 0.1) : isMine ? 'action.selected' : 'var(--surface-muted)',
                                                    outline: isActive ? `2px solid ${theme.palette.primary.main}` : undefined,
                                                    opacity: isDone && !isActive && !dragSnapshot.isDragging ? 0.65 : 1,
                                                    boxShadow: dragSnapshot.isDragging ? 8 : 0
                                                }}>
                                                    {
                                                        // the grip says "this moves"; the whole handle strip is the target, not a tiny icon
                                                        canRun && <Tooltip title="Drag to reorder">
                                                            <Box {...drag.dragHandleProps} aria-label={`Drag to reorder: ${team.title}`}
                                                                sx={{ display: 'flex', alignItems: 'center', alignSelf: 'stretch', px: 0.5, color: 'text.secondary', cursor: busy ? 'default' : 'grab', borderRadius: '8px', '&:hover': { backgroundColor: 'action.hover', color: 'text.primary' }, '&:active': { cursor: 'grabbing' } }}>
                                                                <DragIndicatorIcon />
                                                            </Box>
                                                        </Tooltip>
                                                    }
                                                    {
                                                        canRun ? (
                                                            <Tooltip title={isDone ? 'Mark not presented' : 'Mark presented'}>
                                                                <IconButton
                                                                    size="small"
                                                                    disabled={busy}
                                                                    aria-label={`${isDone ? 'Mark not presented' : 'Mark presented'}: ${team.title}`}
                                                                    onClick={() => togglePresented(id)}>
                                                                    {isDone ? <CheckCircleIcon color="success" /> : <RadioButtonUncheckedIcon />}
                                                                </IconButton>
                                                            </Tooltip>
                                                        ) : (
                                                            <Typography variant="h5" component="span" sx={{ color: 'primary.main', minWidth: 24, textAlign: 'center' }}>
                                                                {isDone ? <CheckCircleIcon color="success" sx={{ verticalAlign: 'middle' }} /> : index + 1}
                                                            </Typography>
                                                        )
                                                    }
                                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                                            <Typography variant="subtitle1" sx={{ overflowWrap: 'anywhere' }}>
                                                                <Link to={`/h/${hackathon.slug}/idea/${id}`} style={{ color: 'inherit' }}>{team.title}</Link>
                                                            </Typography>
                                                            {isActive && <Chip size="small" color="primary" label="Now" />}
                                                            {isMine && <Chip size="small" variant="outlined" label="Your team" />}
                                                            {/* it still presents and is judged; the hosts should see that nobody will speak for it */}
                                                            {team.members.length === 0 && <Chip size="small" color="warning" variant="outlined" label="No members yet" />}
                                                        </Stack>
                                                        {team.members.length > 0 && <Typography variant="body2" color="text.secondary" noWrap>{team.members.map(m => m.username).join(', ')}</Typography>}
                                                    </Box>
                                                    {
                                                        canRun && (
                                                            <Stack direction="row" alignItems="center">
                                                                {!isActive && (
                                                                    <Tooltip title="Present now">
                                                                        <IconButton disabled={busy} aria-label={`Present now: ${team.title}`} onClick={() => apply({ activeIdea: id, presented: live.presented.filter(p => p !== id) })}>
                                                                            <PlayArrowIcon />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                )}
                                                                <IconButton disabled={busy || index === 0} aria-label={`Move up: ${team.title}`} onClick={() => move(index, -1)}>
                                                                    <ArrowUpwardIcon fontSize="small" />
                                                                </IconButton>
                                                                <IconButton disabled={busy || index === order.length - 1} aria-label={`Move down: ${team.title}`} onClick={() => move(index, 1)}>
                                                                    <ArrowDownwardIcon fontSize="small" />
                                                                </IconButton>
                                                                <Tooltip title="Leave out: won't present or be judged">
                                                                    <IconButton disabled={busy} aria-label={`Leave out: ${team.title}`} onClick={() => leaveOut(id)}>
                                                                        <RemoveCircleOutlineIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </Stack>
                                                        )
                                                    }
                                                </Stack>
                                                )}
                                                </Draggable>
                                            );
                                        })}
                                        {drop.placeholder}
                                    </Stack>
                                    )}
                                    </Droppable>
                                    </DragDropContext>
                                )
                        }
                        {
                            live.leftOut.length > 0 && (
                                <Stack spacing={1}>
                                    <Typography variant="subtitle2" color="text.secondary">Not presenting ({live.leftOut.length})</Typography>
                                    {live.leftOut.map(id => {
                                        const team = teamFor(id);
                                        if (!team) {
                                            return null;
                                        }
                                        return (
                                            <Stack key={id} direction="row" spacing={1.5} alignItems="center" sx={{ px: 1.5, py: 0.5, borderRadius: '12px', border: '1px dashed', borderColor: 'divider' }}>
                                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                                    <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                                                        <Link to={`/h/${hackathon.slug}/idea/${id}`} style={{ color: 'inherit' }}>{team.title}</Link>
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary" noWrap component="p">{team.members.map(m => m.username).join(', ') || 'No team yet'}</Typography>
                                                </Box>
                                                {
                                                    canRun && (
                                                        <Button size="small" disabled={busy} startIcon={<AddCircleOutlineIcon />} sx={{ flexShrink: 0 }} aria-label={`Add back: ${team.title}`} onClick={() => putBack(id)}>
                                                            Add back
                                                        </Button>
                                                    )
                                                }
                                            </Stack>
                                        );
                                    })}
                                </Stack>
                            )
                        }
                    </Stack>
                </CardContent>
            </Card>
        </Stack>
    )
}
